import { useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

/** One durable key until a confirmed result. Changed payloads reuse that key, so
 * the server rejects a changed retry if the original request already committed. */
export function usePurchaseIntent(scope: string) {
  const { organization, profile } = useAuth();
  const keys = useRef(new Map<string, string>());
  const prefix = `klothiq.purchase-intent:${organization?.id}:${profile?.id}:${scope}:`;
  const stored = (operation: string) => {
    try {
      return sessionStorage.getItem(prefix + operation);
    } catch {
      return null;
    }
  };
  function complete(operation: string) {
    keys.current.delete(prefix + operation);
    try {
      sessionStorage.removeItem(prefix + operation);
    } catch {
      /* In-memory retry identity still works. */
    }
  }
  return {
    key(operation: string, _payload?: unknown) {
      const key = keys.current.get(prefix + operation) || stored(operation) || crypto.randomUUID();
      keys.current.set(prefix + operation, key);
      try {
        sessionStorage.setItem(prefix + operation, key);
      } catch {
        /* No sensitive payload is persisted. */
      }
      return key;
    },
    complete,
    async recover(operation: string) {
      const key = keys.current.get(prefix + operation) || stored(operation);
      if (!key) return null;
      const { data, error } = await supabase.rpc('purchase_request_result', { p_key: key });
      if (error) throw new Error(error.message);
      if (data) complete(operation);
      return data as { purchase_id?: string; payment_id?: string; receipt_id?: string } | null;
    },
  };
}
